import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["questionKey"];
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { createToObject } from 'pricing-pages-lib/utils/queryParamUtils';
import { useCallback, useEffect } from 'react';
import { Redirect, useLocation } from 'react-router-dom';
import { getPrimaryTracker } from 'usage-tracker-container';
import BannerSection from '../components/banners/BannerSection';
import FallbackErrorBanner from '../components/common/loading/FallbackErrorBanner';
import Faq from '../components/faqs/Faq';
import Question from '../components/faqs/Question';
import HubSpotCreditsHero from '../components/hubspot-credits/HubSpotCreditsHero';
import UnlockAISection from '../components/hubspot-credits/UnlockAISection';
import HeaderLayoutWrapper from '../components/layout/HeaderLayoutWrapper';
import { PRICING_AND_PURCHASING_FAILED_STATUS } from '../constants/ErrorCauses';
import { FaqsConfig } from '../constants/faqs/FaqsConfig';
import { NavItemEnum } from '../constants/routing/NavItems';
import { useAnchorLinkScroll } from '../hooks/htmlElementHooks';
import { useGlobalLoading } from '../hooks/loadingHooks';
import useCreditsPageNavItemTabs from '../hooks/useCreditsPageTabNavItems';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useRedirectIfIneligible } from '../hooks/useRedirectIfIneligible';
import { useAppSelector } from '../reducers';
import { hasGatesLoadedSelector, hasPricingAndPurchasingFailedSelector } from '../selectors/LoadingStateSelectors';
import { hasHubSpotCreditsSkuInProductDataSelector } from '../selectors/ProductsSelectors';
import { useQueryParamTerm, useQueryParamsEffect } from '../utils/urls/appQueryParamUtilsLegacy';
import { ERROR_SEVERITY } from '../v2/components/error-boundary/ErrorSeverity';
import { useErrorBoundary } from '../v2/components/error-boundary/useErrorBoundary';
import withErrorBoundary from '../v2/components/error-boundary/withErrorBoundary';
import { useHighestProductLevelFromInventory } from '../v2/hooks/inventory/useHighestProductLevel';
import { trackPageView } from '../v2/utils/tracking/trackingEventUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const HubSpotCreditsPage = () => {
  useAnchorLinkScroll();
  useDocumentTitle('hubspot-credits');
  const navItem = NavItemEnum.HUBSPOT_CREDITS;
  const faqsConfig = FaqsConfig[navItem];
  const hasHubSpotCreditsSku = useAppSelector(hasHubSpotCreditsSkuInProductDataSelector);
  const isAppReady = useGlobalLoading();
  const hasPricingAndPurchasingFailed = useAppSelector(hasPricingAndPurchasingFailedSelector);
  const hasGatesLoaded = useAppSelector(hasGatesLoadedSelector);
  const {
    queryParamTerm
  } = useQueryParamTerm();
  useQueryParamsEffect({
    term: queryParamTerm
  });
  const {
    showBoundary
  } = useErrorBoundary();

  /**
   * On public, there's a race condition where pricing and purchasing has succeeded, but the activeFeatureFlags have not been populated yet (@see PublicMetaDataActions).
   * So sometimes this condition gets evaluated to true and redirects to /suite before we actually confirm if the user is gated for this page
   */
  useRedirectIfIneligible({
    condition: useCallback(() => {
      if (!hasGatesLoaded) {
        return;
      }
      return !hasHubSpotCreditsSku;
    }, [hasGatesLoaded, hasHubSpotCreditsSku])
  });
  const location = useLocation();
  const queryparams = new URLSearchParams(location.search);
  const tab = queryparams.get('tab') || '';
  const {
    tabs: availableTabs
  } = useCreditsPageNavItemTabs();
  const subscreen = availableTabs.includes(tab) ? tab : availableTabs[0];
  const {
    loading: isInventoryLoading
  } = useHighestProductLevelFromInventory();
  useEffect(() => {
    // Track only after we determine if user is gated/ungated AND what type of cohort they are in
    // This is to accurately track what screen and subscreen the user is viewing
    if (!hasGatesLoaded || isInventoryLoading) return;
    getPrimaryTracker().setProperties({
      defaultedTab: '',
      tab: ''
    });
    trackPageView({
      screen: navItem,
      subscreen
    });
    return () => {
      getPrimaryTracker().setProperties({
        subscreen: ''
      });
    };
  }, [hasGatesLoaded, isInventoryLoading, navItem, subscreen]);
  if (hasPricingAndPurchasingFailed) {
    showBoundary(new Error(PRICING_AND_PURCHASING_FAILED_STATUS));
  }
  if (!isAppReady || !hasGatesLoaded || isInventoryLoading) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {
      minHeight: 200,
      size: "md",
      grow: true
    });
  }
  return /*#__PURE__*/_jsxs(HeaderLayoutWrapper, {
    navItem: navItem,
    navMarkerName: "HUBSPOT_CREDITS_PAGE",
    children: [/*#__PURE__*/_jsx(BannerSection, {
      screen: navItem
    }), /*#__PURE__*/_jsx(FallbackErrorBanner, {}), /*#__PURE__*/_jsx(HubSpotCreditsHero, {
      navItem: navItem
    }), /*#__PURE__*/_jsx(UnlockAISection, {}), /*#__PURE__*/_jsx(Faq, {
      children: faqsConfig.map(_ref => {
        let {
            questionKey
          } = _ref,
          passthroughOptions = _objectWithoutPropertiesLoose(_ref, _excluded);
        return /*#__PURE__*/_jsx(Question, Object.assign({
          questionKey: questionKey
        }, passthroughOptions), questionKey);
      })
    })]
  });
};
export default withErrorBoundary(HubSpotCreditsPage, {
  fallback: /*#__PURE__*/_jsx(Redirect, {
    to: createToObject('/suite')
  }),
  errorBoundaryName: 'HubSpotCreditsPage',
  severity: ERROR_SEVERITY.HIGH
});